import React, { useEffect, useState } from "react";
import { api, formatDate, formatApiErrorDetail } from "../lib/api";
import { Plus, MagnifyingGlass, PencilSimple, Trash, Buildings } from "@phosphor-icons/react";
import { toast } from "sonner";

const TIPI = [
  { v: "azienda", label: "Azienda" },
  { v: "ristorante", label: "Ristorante" },
  { v: "gdo", label: "GDO" },
  { v: "altro", label: "Altro" },
];

const EMPTY = {
  ragione_sociale: "", tipo: "azienda", p_iva: "", codice_fiscale: "",
  indirizzo: "", citta: "", cap: "", provincia: "",
  email: "", telefono: "", referente: "", note: "",
};

export default function Clients() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // null | "new" | client object
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/clients");
      setItems(data);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  function openNew() {
    setForm(EMPTY);
    setEditing("new");
  }
  function openEdit(c) {
    setForm({ ...EMPTY, ...c });
    setEditing(c);
  }
  function close() { setEditing(null); }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/clients", form);
        toast.success("Cliente creato");
      } else {
        await api.put(`/clients/${editing.id}`, form);
        toast.success("Cliente aggiornato");
      }
      close();
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    } finally {
      setSaving(false);
    }
  }

  async function remove(c) {
    if (!window.confirm(`Eliminare cliente "${c.ragione_sociale}"?`)) return;
    try {
      await api.delete(`/clients/${c.id}`);
      toast.success("Cliente eliminato");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    }
  }

  const filtered = items.filter((c) =>
    [c.ragione_sociale, c.p_iva, c.citta, c.referente, c.email]
      .filter(Boolean).join(" ").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-4 md:p-12 max-w-[1400px] mx-auto">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 md:mb-10">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-3">Anagrafica</div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-tighter leading-none">Clienti</h1>
          <p className="text-sm text-[#64748B] mt-3">{items.length} clienti registrati</p>
        </div>
        <button
          onClick={openNew}
          data-testid="new-client-btn"
          className="btn-primary px-5 py-3 text-xs uppercase tracking-[0.15em] font-bold rounded-sm flex items-center gap-2 self-start"
        >
          <Plus size={14} weight="bold" /> Nuovo Cliente
        </button>
      </div>

      <div className="swiss-card overflow-hidden">
        <div className="p-4 border-b border-[#E2E8F0]">
          <div className="relative max-w-md">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
            <input
              placeholder="Cerca per ragione sociale, P.IVA, città..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="client-search-input"
              className="w-full bg-white border border-[#E2E8F0] rounded-sm pl-10 pr-3 py-2 text-sm focus:border-[#0033A0] focus:ring-1 focus:ring-[#0033A0] outline-none"
            />
          </div>
        </div>

        {loading ? (
          <div className="p-10 text-center text-sm text-[#64748B]">Caricamento…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <Buildings size={32} className="mx-auto text-[#64748B] mb-3" />
            <p className="text-sm text-[#64748B] mb-4">
              {items.length === 0 ? "Nessun cliente registrato." : "Nessun risultato."}
            </p>
            {items.length === 0 && (
              <button onClick={openNew} className="text-xs uppercase tracking-[0.15em] font-bold text-[#0033A0] hover:underline">
                Aggiungi il primo cliente
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Tabella desktop */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F5F7]">
                  <tr className="text-left">
                    <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Ragione Sociale</th>
                    <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Tipo</th>
                    <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">P. IVA</th>
                    <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Città</th>
                    <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Referente</th>
                    <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Creato</th>
                    <th className="py-3 px-4 text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => (
                    <tr key={c.id} className="border-t border-[#E2E8F0] hover:bg-[#F4F5F7]" data-testid={`client-row-${c.id}`}>
                      <td className="py-3 px-4 font-semibold">{c.ragione_sociale}</td>
                      <td className="py-3 px-4 text-[#64748B] capitalize">{c.tipo}</td>
                      <td className="py-3 px-4 font-mono text-xs">{c.p_iva || "—"}</td>
                      <td className="py-3 px-4 text-[#64748B]">{c.citta || "—"}</td>
                      <td className="py-3 px-4 text-[#64748B]">{c.referente || "—"}</td>
                      <td className="py-3 px-4 text-[#64748B] text-xs">{formatDate(c.created_at)}</td>
                      <td className="py-3 px-4 text-right">
                        <div className="inline-flex gap-1">
                          <button
                            onClick={() => openEdit(c)}
                            data-testid={`edit-client-${c.id}`}
                            className="p-1.5 text-[#64748B] hover:text-[#0033A0] hover:bg-white rounded-sm"
                            title="Modifica"
                          >
                            <PencilSimple size={16} />
                          </button>
                          <button
                            onClick={() => remove(c)}
                            data-testid={`delete-client-${c.id}`}
                            className="p-1.5 text-[#64748B] hover:text-[#D32F2F] hover:bg-white rounded-sm"
                            title="Elimina"
                          >
                            <Trash size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Card stack mobile */}
            <div className="md:hidden divide-y divide-[#E2E8F0]">
              {filtered.map((c) => (
                <div key={c.id} className="px-4 py-4" data-testid={`client-card-${c.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm truncate">{c.ragione_sociale}</div>
                      <div className="text-[10px] uppercase tracking-[0.15em] text-[#64748B] mt-1">{c.tipo}{c.citta ? ` · ${c.citta}` : ""}</div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => openEdit(c)} className="p-1.5 text-[#64748B] hover:text-[#0033A0]" data-testid={`edit-client-mobile-${c.id}`}><PencilSimple size={18} /></button>
                      <button onClick={() => remove(c)} className="p-1.5 text-[#64748B] hover:text-[#D32F2F]" data-testid={`delete-client-mobile-${c.id}`}><Trash size={18} /></button>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-[#64748B]">
                    {c.p_iva && <span className="font-mono">P.IVA {c.p_iva}</span>}
                    {c.referente && <span>{c.referente}</span>}
                    {c.email && <span className="truncate">{c.email}</span>}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {editing && (
        <ClientModal
          form={form}
          setForm={setForm}
          onClose={close}
          onSave={save}
          saving={saving}
          isNew={editing === "new"}
        />
      )}
    </div>
  );
}

function ClientModal({ form, setForm, onClose, onSave, saving, isNew }) {
  function field(k, label, opts = {}) {
    return (
      <div className={opts.full ? "sm:col-span-2" : ""}>
        <label className="block text-[10px] font-bold uppercase tracking-[0.15em] text-[#0A0A0A] mb-1.5">{label}</label>
        {opts.type === "select" ? (
          <select
            value={form[k] || ""}
            onChange={(e) => setForm({ ...form, [k]: e.target.value })}
            className="w-full bg-white border border-[#E2E8F0] rounded-sm px-3 py-2 text-sm focus:border-[#0033A0] focus:ring-1 focus:ring-[#0033A0] outline-none"
            data-testid={`client-field-${k}`}
          >
            {opts.options.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
        ) : opts.type === "textarea" ? (
          <textarea
            value={form[k] || ""}
            onChange={(e) => setForm({ ...form, [k]: e.target.value })}
            rows={3}
            className="w-full bg-white border border-[#E2E8F0] rounded-sm px-3 py-2 text-sm focus:border-[#0033A0] focus:ring-1 focus:ring-[#0033A0] outline-none"
            data-testid={`client-field-${k}`}
          />
        ) : (
          <input
            type={opts.type || "text"}
            required={opts.required}
            value={form[k] || ""}
            onChange={(e) => setForm({ ...form, [k]: e.target.value })}
            className="w-full bg-white border border-[#E2E8F0] rounded-sm px-3 py-2 text-sm focus:border-[#0033A0] focus:ring-1 focus:ring-[#0033A0] outline-none"
            data-testid={`client-field-${k}`}
          />
        )}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSave}
        className="bg-white border border-[#E2E8F0] rounded-sm max-w-2xl w-full max-h-[90vh] overflow-auto thin-scroll"
        data-testid="client-modal"
      >
        <div className="p-6 border-b border-[#E2E8F0] sticky top-0 bg-white">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-2">{isNew ? "Nuovo" : "Modifica"}</div>
          <h2 className="font-display text-2xl font-bold tracking-tight">Cliente</h2>
        </div>
        <div className="p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {field("ragione_sociale", "Ragione Sociale *", { required: true, full: true })}
          {field("tipo", "Tipo", { type: "select", options: TIPI })}
          {field("referente", "Referente")}
          {field("p_iva", "P. IVA")}
          {field("codice_fiscale", "Codice Fiscale")}
          {field("email", "Email", { type: "email" })}
          {field("telefono", "Telefono")}
          {field("indirizzo", "Indirizzo", { full: true })}
          {field("citta", "Città")}
          {field("provincia", "Provincia")}
          {field("cap", "CAP")}
          {field("note", "Note", { type: "textarea", full: true })}
        </div>
        <div className="p-6 border-t border-[#E2E8F0] flex items-center justify-end gap-3 sticky bottom-0 bg-white">
          <button type="button" onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold border border-[#E2E8F0] rounded-sm hover:bg-[#F4F5F7]" data-testid="client-cancel-btn">
            Annulla
          </button>
          <button type="submit" disabled={saving} className="btn-primary px-5 py-2 text-xs uppercase tracking-[0.15em] font-bold rounded-sm" data-testid="client-save-btn">
            {saving ? "Salvataggio…" : "Salva"}
          </button>
        </div>
      </form>
    </div>
  );
}
